import { useEffect, useState } from 'react';
import AuthScreen     from './screens/AuthScreen.jsx';
import OnboardingScreen from './screens/OnboardingScreen.jsx';
import TrackSelector  from './screens/TrackSelector.jsx';
import Dashboard      from './screens/Dashboard.jsx';
import AffiliatePage  from './screens/AffiliatePage.jsx';
import AdminShell     from './screens/admin/AdminShell.jsx';
import LandingPage    from './screens/LandingPage.jsx';
import ContactPage    from './screens/ContactPage.jsx';
import FollowJourneyPage from './screens/FollowJourneyPage.jsx';
import PrivacyPage      from './screens/PrivacyPage.jsx';
import TermsPage        from './screens/TermsPage.jsx';
import QrPage           from './screens/QrPage.jsx';
import AccountPage       from './screens/AccountPage.jsx';
import ResetPassword     from './screens/ResetPassword.jsx';
import PersonalizationFlow from './screens/onboarding/PersonalizationFlow.jsx';
import { supabase, getLearner, updateLearnerProfile } from './services/supabaseClient.js';
import { recordPageView } from './utils/pageview.js';
import { captureUtmParams } from './utils/track.js';

const SCREENS = {
  AUTH:            'AUTH',
  ONBOARDING:      'ONBOARDING',
  TRACKS:          'TRACKS',           // authenticated dashboard at "/"
  TRACKS_SELECTOR: 'TRACKS_SELECTOR',  // full track-browser screen
  // NOTE: there is no standalone STUDY screen. Study always renders embedded
  // inside the Dashboard shell so the sidebar is present no matter where the
  // lesson was started from (sidebar nav, Start Lesson CTA, track selector, …).
};

export default function App() {
  // Standalone public routes — no auth, no session check, no sidebar.
  // Path is lowercased so /Start, /Investor, /JOURNEY etc. all match
  // their canonical lowercase route ids. Every comparison below uses this
  // normalized value — one change here covers them all.
  const path = typeof window !== 'undefined'
    ? window.location.pathname.replace(/\/$/, '').toLowerCase()
    : '';
  const isAffiliate = path === '/affiliate';
  const isAdmin = path === '/admin' || path.startsWith('/admin/');
  const isSignin = path === '/signin';
  // QA/admin test-account entry: /signin?signup=1 must reach the Create Account
  // form even when an admin session already exists in this browser. Without
  // this, the session resolver below loads that session and the render
  // redirects /signin → / (dashboard), so the bypass form never shows. Public
  // signup stays gated by SIGNUP_ENABLED in AuthScreen; this only affects the
  // explicit ?signup=1 link.
  const signupBypass = isSignin && typeof window !== 'undefined'
    && new URLSearchParams(window.location.search).get('signup') === '1';
  const isContact = path === '/contact';
  const isJourney = path === '/journey' || path === '/follow-the-journey';
  const isPrivacy = path === '/privacy';
  const isTerms   = path === '/terms';
  const isResetPassword = path === '/reset-password';   // public — Supabase recovery flow
  // /start is retired. The QR/NFC entry now points straight at the homepage with
  // ?ref=qr. Any already-printed /start media still works: redirect it to the
  // tagged homepage so the scan keeps its QR attribution instead of 404-ing into
  // the untagged landing page.
  const isStart       = path === '/start';
  const isOnboarding  = path === '/registration' || path === '/onboarding';
  const isQr          = path === '/qr';
  const isAccount     = path === '/account';   // auth-required; resolver runs, redirect to /signin if no session
  const [screen,   setScreen]   = useState(SCREENS.AUTH);
  const [learner,  setLearner]  = useState(null);
  // When set, the Dashboard opens the embedded (sidebar-present) Study console
  // for this track id. Starting a lesson never leaves the dashboard shell.
  const [studyTrackId, setStudyTrackId] = useState(null);
  // Mandatory onboarding gate: when an authenticated learner has not yet
  // personalized (onboarding_completed_at is null), force them through the
  // PersonalizationFlow before any dashboard/lesson access. Held in its own
  // flag (not keyed directly on onboarding_completed_at) so the flow's final
  // "here's your path" recommendation screen survives after completion stamps
  // the timestamp — it's cleared only by Start-lesson / View-roadmap.
  const [forceOnboarding, setForceOnboarding] = useState(false);
  const [checking, setChecking] = useState(true);
  // Theme is locked to the light theme we built — the dark/light selector was removed.
  const theme = 'light';

  useEffect(() => {
    document.documentElement.setAttribute('data-theme', 'light');
    localStorage.setItem('adaptos-theme', 'light');
  }, []);

  // First-party page-view beacon — one per full page load (router does full
  // reloads, so this captures every route view). Fire-and-forget. UTM + ref
  // capture runs alongside so attribution lands before any track() call.
  useEffect(() => {
    captureUtmParams();
    recordPageView();
  }, []);

  const toggleTheme = () => {};   // no-op — theme is locked to light

  useEffect(() => {
    if (isAffiliate || isAdmin || isContact || isJourney || isPrivacy || isTerms || isResetPassword || isStart || isOnboarding || isQr || signupBypass) { setChecking(false); return; }
    supabase.auth.getSession().then(async ({ data }) => {
      try {
        if (data.session?.user) {
          const user    = data.session.user;
          const profile = await getLearner(user.id);
          setLearner({
            id:            user.id,
            name:          profile?.name          || user.email,
            first_name:    profile?.first_name    || null,
            last_name:     profile?.last_name     || null,
            email:         user.email,
            experience:    profile?.experience    || null,
            concern:       profile?.concern       || null,
            learning_mode: profile?.learning_mode || null,
            target_date:   profile?.target_date   || null,
            current_track: profile?.current_track || null,
            cert_path:               Array.isArray(profile?.cert_path) ? profile.cert_path : null,
            confidence_baseline:     profile?.confidence_baseline     || null,
            study_hours:             profile?.study_hours             || null,
            onboarding_completed_at: profile?.onboarding_completed_at || null,
            lesson_1_engaged_at:     profile?.lesson_1_engaged_at     || null,
            created_at:              profile?.created_at              || null,
            // Stripe subscription state — drives lesson-2/3 trial nudges + access gating.
            subscription_status:     profile?.subscription_status     || 'none',
            trial_end:               profile?.trial_end               || null,
            stripe_customer_id:      profile?.stripe_customer_id      || null,
          });
          // Onboarding is mandatory: an authenticated learner who has not yet
          // personalized is forced through the PersonalizationFlow (the gate
          // below) before the dashboard — so the track comes from their goal,
          // not a silent A+ default, and they understand why we start there.
          if (!profile?.onboarding_completed_at) setForceOnboarding(true);
          setScreen(SCREENS.TRACKS);
        }
      } catch {
        await supabase.auth.signOut();
      } finally {
        setChecking(false);
      }
    });
  }, []);

  const handleAuth = (learnerData) => {
    setLearner(learnerData);
    // Mandatory onboarding: a learner who hasn't personalized is gated into the
    // PersonalizationFlow before the dashboard.
    if (!learnerData?.onboarding_completed_at) setForceOnboarding(true);
    setScreen(SCREENS.TRACKS);
  };

  const handleOnboardingComplete = (updatedLearner) => {
    setLearner(updatedLearner);
    // Just finished account signup — send them straight into personalization.
    if (!updatedLearner?.onboarding_completed_at) setForceOnboarding(true);
    setScreen(SCREENS.TRACKS);
  };

  // Merge personalization-flow results into the in-memory learner so the
  // Dashboard + Roadmap nudge cards disappear and the Roadmap re-renders
  // personalized (career_goal, track, onboarding_completed_at) without a reload.
  const handleLearnerUpdate = (updates) => {
    setLearner(prev => (prev ? { ...prev, ...updates } : prev));
  };

  const handleSelectTrack = async (selectedTrack) => {
    if (learner?.id && selectedTrack?.id) {
      await updateLearnerProfile(learner.id, { current_track: selectedTrack.id });
      setLearner(prev => prev ? { ...prev, current_track: selectedTrack.id } : prev);
    }
    // Open Study embedded in the dashboard shell (sidebar stays visible) rather
    // than a full-screen takeover — this is the single, only way a lesson opens.
    setStudyTrackId(selectedTrack?.id || null);
    setScreen(SCREENS.TRACKS);
  };

  // Selection-only: persist current_track without leaving the dashboard.
  // Used by the dashboard TrackGrid where click = select, not open.
  const handleTrackChange = async (trackId) => {
    if (!learner?.id || !trackId) return;
    await updateLearnerProfile(learner.id, { current_track: trackId });
    setLearner(prev => prev ? { ...prev, current_track: trackId } : prev);
  };

  // Route the "Browse All Tracks" button to the standalone TrackSelector screen.
  // handleSelectTrack (which opens the embedded Study console) still owns
  // "actually start a track."
  const handleBrowseTracks = () => setScreen(SCREENS.TRACKS_SELECTOR);
  const handleBackToDashboard = () => setScreen(SCREENS.TRACKS);

  const handleSignOut = async () => {
    await supabase.auth.signOut();
    setLearner(null);
    setStudyTrackId(null);
    setScreen(SCREENS.AUTH);
  };

  if (isAffiliate) {
    return <AffiliatePage/>;
  }

  if (isAdmin) {
    return <AdminShell/>;
  }

  if (isContact) {
    return <ContactPage/>;
  }

  if (isJourney) {
    return <FollowJourneyPage/>;
  }

  if (isPrivacy) {
    return <PrivacyPage/>;
  }

  if (isResetPassword) {
    return <ResetPassword/>;
  }
  if (isTerms) {
    return <TermsPage/>;
  }

  if (isStart) {
    if (typeof window !== 'undefined') {
      const ref = new URLSearchParams(window.location.search).get('ref');
      window.location.replace(ref ? `/?ref=${encodeURIComponent(ref)}` : '/?ref=qr');
    }
    return null;
  }

  if (isQr) {
    return <QrPage/>;
  }

  if (isOnboarding) {
    // /onboarding is the public entry to the new-signup flow. No learner
    // prop → OnboardingScreen runs Screen 1 (full signup) → Screen 2.
    // onComplete returns to "/" so App.jsx re-resolves auth and routes to
    // the dashboard for the freshly-signed-up learner.
    return (
      <OnboardingScreen
        onComplete={() => window.location.assign('/')}
        theme={theme}
        toggleTheme={toggleTheme}
      />
    );
  }

  if (checking) {
    return (
      <div style={{ minHeight: '100vh', background: 'var(--bg-primary)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <div style={{ width: 10, height: 10, borderRadius: '50%', background: 'var(--accent-primary)', animation: 'orb 1s ease-in-out infinite' }}/>
        <style>{`@keyframes orb{0%,100%{opacity:.3;transform:scale(.7)}50%{opacity:1;transform:scale(1.1)}}`}</style>
      </div>
    );
  }

  // /account — auth-required. The session resolver above has finished (checking
  // is false here); render the page if we have a learner, otherwise bounce to
  // /signin. Placed before the screen-based branches so it works regardless of
  // which screen the onboarding gate resolved to.
  if (isAccount) {
    if (!learner) {
      if (typeof window !== 'undefined') window.location.replace('/signin');
      return null;
    }
    return <AccountPage learner={learner} onSignOut={handleSignOut} theme={theme} toggleTheme={toggleTheme}/>;
  }

  if (screen === SCREENS.ONBOARDING) {
    return (
      <OnboardingScreen learner={learner} onComplete={handleOnboardingComplete} theme={theme} toggleTheme={toggleTheme}/>
    );
  }

  // Mandatory onboarding gate — a personalized track + cert path before any
  // dashboard/lesson access. The flow stamps onboarding_completed_at and sets
  // current_track via recommendCert (no silent A+ default). Only completion
  // (Start lesson / View roadmap) or sign-out exits: a pre-completion Cancel
  // signs out rather than dropping into an un-personalized dashboard.
  if (learner && forceOnboarding) {
    return (
      <PersonalizationFlow
        learner={learner}
        onLearnerUpdate={handleLearnerUpdate}
        onStartLesson={() => { setForceOnboarding(false); setStudyTrackId(learner.current_track || null); setScreen(SCREENS.TRACKS); }}
        onExit={() => {
          if (learner?.onboarding_completed_at) { setForceOnboarding(false); setScreen(SCREENS.TRACKS); }
          else { handleSignOut(); }
        }}
      />
    );
  }

  if (screen === SCREENS.TRACKS) {
    // The dashboard lives at "/". An authenticated user who deep-links to
    // /signin has no business on the auth form — send them to the dashboard.
    if (isSignin) {
      window.location.replace('/');
      return null;
    }
    return (
      <div style={{ height: '100vh', width: '100vw', overflow: 'hidden', fontFamily: "'Inter','Segoe UI',sans-serif" }}>
        <Dashboard
          learner={learner}
          onSelectTrack={handleSelectTrack}
          onTrackChange={handleTrackChange}
          onBrowseTracks={handleBrowseTracks}
          onSignOut={handleSignOut}
          onLearnerUpdate={handleLearnerUpdate}
          studyTrackId={studyTrackId}
          onStudyExit={() => setStudyTrackId(null)}
          theme={theme}
          toggleTheme={toggleTheme}
        />
      </div>
    );
  }

  if (screen === SCREENS.TRACKS_SELECTOR) {
    return (
      <div style={{ height: '100vh', width: '100vw', overflow: 'hidden', fontFamily: "'Inter','Segoe UI',sans-serif" }}>
        <TrackSelector
          learner={learner}
          onSelectTrack={handleSelectTrack}
          onBackToDashboard={handleBackToDashboard}
          onSignOut={handleSignOut}
          theme={theme}
          toggleTheme={toggleTheme}
        />
      </div>
    );
  }

  // Unauthenticated. "/signin" shows the auth form; everything else at the root
  // shows the public landing page.
  if (isSignin) {
    return (
      <div style={{ minHeight: '100vh', width: '100vw', background: 'var(--bg-primary)', fontFamily: "'Inter','Segoe UI',sans-serif" }}>
        <AuthScreen onAuth={handleAuth}/>
      </div>
    );
  }

  return <LandingPage/>;
}
